"use client";

import { useEffect, useRef, useState } from "react";
import { Logo } from "@/components/Logo";
import Link from "next/link";
import { GateButton, useGate } from "./GateProvider";

// ─────────────────────────────────────────────────────────────────────────────
// The reference's remaining JS behaviours, as components. Each one is a direct
// port — same thresholds, same timings, same class toggles.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * nav.stuck when scrollY > 8.
 *
 * Under 820px the four link items (hide-m) collapse; a toggle button opens
 * a dropdown panel carrying the same links, since app/home.css hides them
 * with no on-screen replacement otherwise.
 */
export function Nav() {
  const [stuck, setStuck] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => {
    const onScroll = () => setStuck(scrollY > 8);
    onScroll();
    addEventListener("scroll", onScroll, { passive: true });
    return () => removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMenuOpen(false);
    };
    addEventListener("keydown", onKey);
    return () => removeEventListener("keydown", onKey);
  }, [menuOpen]);

  const closeMenu = () => setMenuOpen(false);

  return (
    <nav className={stuck ? "stuck" : undefined}>
      <div className="wrap nav-in">
        <Link className="logo" href="/">
          <Logo size={26} />
          <span>
            MakeMySite<b>Today</b>
          </span>
        </Link>
        <div className="nav-right">
          <ul className="nav-links">
            <li className="hide-m">
              <a href="#templates">Templates</a>
            </li>
            <li className="hide-m">
              <a href="#how">How it works</a>
            </li>
            <li className="hide-m">
              <a href="#pricing">Pricing</a>
            </li>
            <li className="hide-m">
              <Link href="/login">Login</Link>
            </li>
            <li>
              <GateButton source="nav" className="btn btn-primary btn-sm">
                <span className="cta-full">Build Free →</span>
                <span className="cta-short">Build →</span>
              </GateButton>
            </li>
          </ul>
          <button
            type="button"
            className="nav-toggle"
            aria-expanded={menuOpen}
            aria-controls="mobile-nav-panel"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((o) => !o)}
          >
            <span />
            <span />
            <span />
          </button>
        </div>
      </div>
      {menuOpen && (
        <div className="mobile-nav-panel" id="mobile-nav-panel">
          <a href="#templates" onClick={closeMenu}>
            Templates
          </a>
          <a href="#how" onClick={closeMenu}>
            How it works
          </a>
          <a href="#pricing" onClick={closeMenu}>
            Pricing
          </a>
          <Link href="/login" onClick={closeMenu}>
            Login
          </Link>
        </div>
      )}
    </nav>
  );
}

/**
 * IntersectionObserver reveal — adds .in at threshold .12, then unobserves.
 *
 * The class must land ON the revealed element, never on a wrapper: in the
 * reference `reveal` is co-classed (`<div class="card reveal">`), and cards,
 * steps, feats and plans are grid items. An extra wrapper would make the
 * wrapper the grid item and stop them stretching to equal height.
 */
export function useReveal<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (es) => {
        es.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return ref;
}

export function Reveal({
  children,
  className = "",
  style,
}: {
  children: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`${className} reveal`.trim()} style={style}>
      {children}
    </div>
  );
}

type Scene = { url: string; node: React.ReactNode };

/** Hero mockup — cycles scenes every 3800ms, syncing the URL bar. */
export function HeroMock() {
  const scenes: Scene[] = [
    {
      url: "aisha-and-rohan.makemysitetoday.com",
      node: (
        <>
          <div className="fam">Together with their families</div>
          <div className="names">Aisha &amp; Rohan</div>
          <div className="date">December 14, 2026 · Udaipur</div>
          <div className="menu">
            <span>Our Story</span>
            <span>Events</span>
            <span>RSVP</span>
          </div>
        </>
      ),
    },
    {
      url: "green-leaf-store.makemysitetoday.com",
      node: (
        <>
          <div className="shopname">🌿 Green Leaf Store</div>
          <div className="tag">Organic groceries · Banjara Hills</div>
          <div className="items">
            <div className="it">
              <b>🥭</b>Fresh fruit
            </div>
            <div className="it">
              <b>🍚</b>Grains &amp; staples
            </div>
            <div className="it">
              <b>🧴</b>Cold-pressed oils
            </div>
          </div>
          <div className="hrs">OPEN 8:00–21:00 · MON–SAT</div>
        </>
      ),
    },
    {
      url: "meera-kapoor.makemysitetoday.com",
      node: (
        <>
          <div className="pname">Meera Kapoor</div>
          <div className="prole">Illustrator &amp; brand designer</div>
          <div className="grid">
            <span className="cell c1" />
            <span className="cell c2" />
            <span className="cell c3" />
            <span className="cell c4" />
            <span className="cell c5" />
            <span className="cell c6" />
          </div>
          <div className="see">See all work →</div>
        </>
      ),
    },
  ];
  const classes = ["s-wed", "s-shop", "s-port"];
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    if (matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % 3), 3800);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="mock" aria-hidden="true">
      <div className="mock-bar">
        <div className="dots">
          <i />
          <i />
          <i />
        </div>
        <div className="urlbar">{scenes[idx].url}</div>
      </div>
      <div className="mock-stage">
        {scenes.map((s, i) => (
          <div
            key={i}
            className={`scene ${classes[i]}${i === idx ? " on" : ""}`}
          >
            {s.node}
          </div>
        ))}
        <div className="pub">
          <span className="pulse" /> Published · live now
        </div>
      </div>
    </div>
  );
}

/**
 * Proof strip. Honest by construction (CLAUDE.md: never fake a counter):
 * the count is the real weekly number from the DB and only appears once it
 * clears THRESHOLD; below that the bar carries no number at all.
 */
export function ProofStrip({
  weeklyCount,
  threshold,
  marquee,
}: {
  weeklyCount: number;
  threshold: number;
  marquee: string[];
}) {
  const showCount = weeklyCount >= threshold;
  const [n, setN] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  // Animated counter — 1400ms ease-out cubic, exactly as the reference.
  useEffect(() => {
    if (!showCount) return;
    const el = ref.current;
    if (!el) return;
    let started = false;
    const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && !started) {
        started = true;
        if (reduced) {
          setN(weeklyCount);
          return;
        }
        const t0 = performance.now();
        const tick = (now: number) => {
          const p = Math.min((now - t0) / 1400, 1);
          setN(Math.round(weeklyCount * (1 - Math.pow(1 - p, 3))));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    });
    io.observe(el);
    return () => io.disconnect();
  }, [showCount, weeklyCount]);

  return (
    <div className="proof">
      <div className="wrap proof-in">
        <div className="count" ref={ref}>
          {showCount ? (
            <>
              <b>{n.toLocaleString("en-IN")}</b> demo sites built this week
            </>
          ) : (
            "Join the makers building their sites today"
          )}
        </div>
        <div className="marq" aria-hidden="true">
          <div className="marq-track">
            {[...marquee, ...marquee].map((m, i) => (
              <span key={i}>{m}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Sticky mobile CTA — shows past 90vh, dismissible. */
export function StickyCta() {
  const [show, setShow] = useState(false);
  const dismissed = useRef(false);
  useEffect(() => {
    const onScroll = () => {
      if (dismissed.current) return;
      setShow(scrollY > innerHeight * 0.9);
    };
    addEventListener("scroll", onScroll, { passive: true });
    return () => removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div className={`sticky-cta${show ? " show" : ""}`}>
      <span style={{ fontWeight: 600, fontSize: "14.5px" }}>
        3 free demo sites 🎁
      </span>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <GateButton source="sticky" className="go">
          Build Free →
        </GateButton>
        <button
          className="x"
          aria-label="Dismiss"
          onClick={() => {
            dismissed.current = true;
            setShow(false);
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}

/** A showcase card — the whole card opens the gate with its category tagged. */
export function TemplateCard({
  category,
  url,
  previewClass,
  preview,
  title,
  live,
  body,
}: {
  category: string;
  url: string;
  previewClass: string;
  preview: React.ReactNode;
  title: React.ReactNode;
  live: boolean;
  body: string;
}) {
  const { openGate } = useGate();
  const ref = useReveal<HTMLDivElement>();
  return (
    <>
      <div
        ref={ref}
        className="card reveal"
        role="button"
        tabIndex={0}
        onClick={() => openGate(category, "template")}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            openGate(category, "template");
          }
        }}
      >
        <div className="prev">
          <div className="prev-url">{url}</div>
          <div className={`inner ${previewClass}`}>{preview}</div>
          <div className="hoverlay">
            <span className="btn">
              {live ? "Build this — free →" : "Get early access →"}
            </span>
          </div>
        </div>
        <div className="card-body">
          <h3>
            {title}{" "}
            <span className={`pill ${live ? "pill-live" : "pill-early"}`}>
              {live ? "Live" : "Early access"}
            </span>
          </h3>
          <p>{body}</p>
        </div>
      </div>
    </>
  );
}
